import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { forwardRef, useState, useEffect, useImperativeHandle, useRef } from 'react';
import { createPortal } from 'react-dom';
import { themeGet } from '@primer/react';
import styled from 'styled-components';
import { LTText, LTBox, LTDivider, LTIconButton, addAriaPropertiesOnBackgroundEls } from '@lambdatestincprivate/lt-components';

/**
 * Local fork of lt-components LTDrawer (0.3.17) — PORT of
 * apps/kaneai-test-management-client/src/components/common/LTDrawerFixed
 * (keep the two in sync; see the EVIDENCE-VIEWER PARITY marker there).
 *
 * Fixes the open-but-blank race: the upstream `setVisibility(false)` schedules
 * `setTimeout(setIsVisible(false), 300)` for the close animation but never
 * stores or clears the timer. Mounting closed then opening within 300ms (or
 * close-then-reopen within 300ms) lets the stale timer fire after open,
 * unmounting the body while `open` stays true. A wait can't fix it — the timer
 * is internal. Here the pending timer is cleared on every visibility change and
 * on unmount.
 *
 * Hit in the viewer by the palette's "Open Context sources" from another tab:
 * goTab('coverage') mounts CoverageTab and the focus channel opens the drawer
 * in the same commit — well inside the stale 300ms window.
 */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var LTDrawerPosition = /*#__PURE__*/function (LTDrawerPosition) {
  LTDrawerPosition["LEFT"] = "left";
  LTDrawerPosition["RIGHT"] = "right";
  return LTDrawerPosition;
}(LTDrawerPosition || {});
var XIcon = function XIcon() {
  return /*#__PURE__*/_jsx("svg", {
    "aria-hidden": "true",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 16 16",
    width: "16",
    height: "16",
    role: "img",
    style: {
      display: 'block',
      margin: '0 auto',
      fill: 'rgb(102,102,102)'
    },
    children: /*#__PURE__*/_jsx("path", {
      d: "M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.749.749 0 0 1 1.275.326.749.749 0 0 1-.215.734L9.06 8l3.22 3.22a.749.749 0 0 1-.326 1.275.749.749 0 0 1-.734-.215L8 9.06l-3.22 3.22a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z"
    })
  });
};
var Wrapper = styled(LTBox).withConfig({
  displayName: 'LTDrawer__Wrapper',
  componentId: 'sc-1cn8os7-0'
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n  color: var(--lt-text-primary);\n  padding: ", ";\n"])), themeGet('space.3'));
var DrawerHeader = styled.div.withConfig({
  displayName: 'LTDrawer__DrawerHeader',
  componentId: 'sc-1cn8os7-1'
})(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  width: 100%;\n"])));
var Content = styled.div.withConfig({
  displayName: 'LTDrawer__Content',
  componentId: 'sc-1cn8os7-2'
})(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  padding: 16px;\n  height: 100%;\n  @media only screen and (max-width: 1024px) {\n    overflow: auto;\n  }\n"])));
var Backdrop = styled.div.withConfig({
  displayName: 'LTDrawer__Backdrop',
  componentId: 'sc-1cn8os7-3'
})(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  background-color: rgba(140, 140, 140, 0.2);\n  z-index: 100;\n  position: fixed;\n  top: 0;\n  left: 0;\n  height: 100%;\n  width: 100vw;\n  opacity: ", ";\n  transition: opacity 0.3s ease-in-out;\n  pointer-events: ", ";\n"])), function (props) {
  return props.open ? 1 : 0;
}, function (props) {
  return props.open ? 'auto' : 'none';
});
var Footer = styled.div.withConfig({
  displayName: 'LTDrawer__Footer',
  componentId: 'sc-1cn8os7-4'
})(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  bottom: 0px;\n  position: absolute;\n  width: 100%;\n"])));
var Drawer = styled.div.withConfig({
  displayName: 'LTDrawer__Drawer',
  componentId: 'sc-1cn8os7-5'
})(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n  background-color: var(--lt-bg-base);\n  z-index: 99999;\n  position: fixed;\n  height: ", ";\n  ", "\n  top: ", ";\n  bottom: 0;\n  width: ", ";\n  transform: ", ";\n  transition: transform 0.3s ease-in-out;\n  display: flex;\n  flex-direction: column;\n  box-shadow: 0px 8px 24px 0px rgba(66, 74, 83, 0.12),\n    0px 1px 3px 0px rgba(31, 35, 40, 0.12);\n  border: ", ";\n  border-radius: ", ";\n  border-color: var(--lt-border-secondary);\n"])), function (props) {
  var _props$top;
  return "calc(100vh - ".concat((_props$top = props.top) !== null && _props$top !== void 0 ? _props$top : 0, "px)");
}, function (props) {
  return props.position === LTDrawerPosition.LEFT ? 'left: 0;' : 'right: 0;';
}, function (props) {
  var _props$top2;
  return "".concat((_props$top2 = props.top) !== null && _props$top2 !== void 0 ? _props$top2 : 0, "px");
}, function (props) {
  var _props$width;
  return (_props$width = props.width) !== null && _props$width !== void 0 ? _props$width : '300px';
}, function (props) {
  if (props.position === LTDrawerPosition.LEFT) {
    return props.open ? 'translateX(0)' : 'translateX(-100%)';
  }
  return props.open ? 'translateX(0)' : 'translateX(100%)';
}, themeGet('borderWidths.1'), function (props) {
  return props.position === LTDrawerPosition.LEFT ? '0px 12px 12px 0px' : '12px 0px 0px 12px';
});
var LTDrawerBase = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var bodyStyle = _ref.bodyStyle,
    _ref$closable = _ref.closable,
    closable = _ref$closable === void 0 ? true : _ref$closable,
    customHeader = _ref.customHeader,
    _ref$drawerContainer = _ref.drawerContainer,
    drawerContainer = _ref$drawerContainer === void 0 ? null : _ref$drawerContainer,
    footerContent = _ref.footerContent,
    footerStyle = _ref.footerStyle,
    headerStyle = _ref.headerStyle,
    noPortal = _ref.noPortal,
    title = _ref.title,
    subTitle = _ref.subTitle,
    showFooter = _ref.showFooter,
    content = _ref.content,
    leadingIcon = _ref.leadingIcon,
    onClose = _ref.onClose,
    width = _ref.width,
    _ref$position = _ref.position,
    position = _ref$position === void 0 ? LTDrawerPosition.RIGHT : _ref$position,
    top = _ref.top,
    _ref$closeBtnClassNam = _ref.closeBtnClassName,
    closeBtnClassName = _ref$closeBtnClassNam === void 0 ? 'drawer_close_btn' : _ref$closeBtnClassNam,
    _ref$dataTestId = _ref.dataTestId,
    dataTestId = _ref$dataTestId === void 0 ? '' : _ref$dataTestId;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  // used for smoother animation when closing the drawer
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isVisible = _useState4[0],
    setIsVisible = _useState4[1];
  var closeTimerRef = useRef(null);
  var clearCloseTimer = function clearCloseTimer() {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  var setVisibility = function setVisibility(visible) {
    // Always kill any pending close-timer first so a stale one can't
    // clobber an open that happened within the 300ms window.
    clearCloseTimer();
    if (visible) {
      setIsVisible(true);
    } else {
      closeTimerRef.current = setTimeout(function () {
        setIsVisible(false);
        closeTimerRef.current = null;
      }, 300); // Delay for smooth drawer close animation
    }
  };
  useEffect(function () {
    setVisibility(open);
    if (noPortal) {
      return undefined;
    }
    addAriaPropertiesOnBackgroundEls(!open, drawerContainer);
    return function () {
      clearCloseTimer();
      if (!noPortal) {
        addAriaPropertiesOnBackgroundEls(true, drawerContainer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, noPortal]);
  useImperativeHandle(ref, function () {
    return {
      openDrawer: function openDrawer() {
        return setOpen(true);
      },
      closeDrawer: function closeDrawer() {
        return setOpen(false);
      }
    };
  });
  var showHeader = leadingIcon || title || customHeader || subTitle;
  var drawerContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Backdrop, {
      open: open,
      onClick: function onClick() {
        setOpen(false);
        onClose && onClose();
      }
    }), /*#__PURE__*/_jsx(Drawer, {
      top: top,
      open: open,
      width: width,
      position: position,
      children: isVisible && /*#__PURE__*/_jsxs(_Fragment, {
        children: [showHeader && /*#__PURE__*/_jsxs(Wrapper, {
          children: [!!leadingIcon && leadingIcon, /*#__PURE__*/_jsxs(DrawerHeader, {
            style: _objectSpread({
              justifyContent: title ? 'space-between' : 'end'
            }, headerStyle),
            children: [title && /*#__PURE__*/_jsx(LTText, {
              variant: LTText.TextVariant.SUBHEADER_BOLD,
              children: title
            }), customHeader && customHeader, closable && /*#__PURE__*/_jsx(LTIconButton, {
              className: closeBtnClassName,
              ariaLabel: "Close Drawer",
              "data-testid": dataTestId ? "".concat(dataTestId, "-close-btn") : 'lt-drawer-close-btn',
              icon: XIcon,
              variant: LTIconButton.IconButtonVariants.INVISIBLE,
              onClick: function onClick() {
                setOpen(false);
                onClose && onClose();
                if (noPortal) {
                  return;
                }
                addAriaPropertiesOnBackgroundEls(true, drawerContainer);
              }
            })]
          }), subTitle && /*#__PURE__*/_jsx(LTText, {
            variant: LTText.TextVariant.SMALL_REGULAR,
            style: {
              color: '#57606A'
            },
            children: subTitle
          })]
        }), showHeader && /*#__PURE__*/_jsx(LTDivider, {}), /*#__PURE__*/_jsx(Content, {
          style: bodyStyle,
          children: content
        }), showFooter && /*#__PURE__*/_jsxs(Footer, {
          children: [/*#__PURE__*/_jsx(LTDivider, {}), /*#__PURE__*/_jsx(Content, {
            style: footerStyle,
            children: footerContent
          })]
        })]
      })
    })]
  });
  return /*#__PURE__*/_jsx(_Fragment, {
    children: noPortal ? drawerContent : /*#__PURE__*/createPortal(/*#__PURE__*/_jsx("div", {
      className: "lt-modal-wrapper",
      children: drawerContent
    }), drawerContainer || document.body)
  });
});
LTDrawerBase.displayName = 'LTDrawer';
var LTDrawer = LTDrawerBase;
LTDrawer.Position = LTDrawerPosition;
export { LTDrawer };